import {atom} from 'jotai';
import {apolloClient} from '@/lib/apolloClient';
import {RunTicket_AttachPhotoDocument, type RunTicket_AttachPhotoMutation} from '@/graphql/generated/graphql.ts';
import {getAllPendingPhotos, deletePendingPhoto, type PendingPhoto} from '@/lib/offlineDb';
import {uploadRunTicketPhoto} from '@/lib/runTicketPhotoUpload';
import {toRunTicket} from '@/lib/runTicketMapping';
import type {RunTicket} from '@/types/well';
import {runTicketsAtom} from './runTicketAtoms';
import {pushNotificationAtom} from './notificationAtoms';

// CHANGED 2026-09-10 (Duane, via a captured network trace showing
// RunTicket_AttachPhoto's response coming back with scanStatus:
// 'SCANNED' and all the OCR-extracted fields — purchaser,
// transportationCompany, pickupDate, the gauges, etc. — populated):
// this used to resolve to a bare `string | undefined` (the imageLocation
// on success, undefined on permanent failure). That threw away exactly
// the data callers need to show a freshly-scanned ticket without a
// second round trip — RunTicket_AttachPhoto.graphql's selection was
// widened the same day to actually carry those fields back (previously
// it asked only for id/scanStatus/ticketImageData, see that file's own
// note), so this now maps the widened response through toRunTicket and
// hands the full ticket back to the caller instead of just the S3 key.
export type PhotoSyncResult =
   | {status: 'attached'; runTicketId: string; imageLocation: string; ticket: RunTicket}
   | {status: 'failed'; runTicketId: string};

export type PhotoSyncState = 'idle' | 'syncing' | 'error';
export const photoSyncStateAtom = atom<PhotoSyncState>('idle');

// De-dupe guard for concurrent syncs of the SAME photo, keyed by
// entityKey — added 2026-08-21 while ruling out a client-side cause for
// Duane's report that a scanned photo wasn't there for the server to
// scan despite the RunTicket getting the correct imageLocation and the
// photo genuinely landing in S3.
//
// Real race this closes: ScanTicketButton/RunTicketFormPanel fire an
// early best-effort sync the INSTANT a photo is captured
// (`syncPendingPhoto(...).catch(() => {})`, not awaited by anything —
// see their own handlePhotoChange/handleFileChange). If the user
// submits before that upload finishes (very plausible on a slow field
// connection — this is exactly the kind of upload that's often taken
// outdoors near a well), Submit Report's own syncAllPendingRunTicketPhotos()
// call reads the same still-pending photo out of IndexedDB and starts a
// SECOND, fully independent upload+attach for it — two concurrent
// uploads to the same deterministic S3 key, two concurrent
// runTicket_CreateUpdate calls for the same ticket, racing each other.
// Without this guard, a caller has no way to know "the photo is already
// being uploaded right now" and no way to just wait for that instead of
// starting a redundant one of its own.
//
// This map makes every caller for the same entityKey share the exact
// same in-flight Promise — so there's only ever one upload and one
// attach mutation per photo, and anyone awaiting the return (Submit
// Report included) is now guaranteed to be awaiting the SAME operation,
// not racing a second one.
const inFlightSyncs = new Map<string, Promise<PhotoSyncResult>>();

// How long a photo can sit with no matching RunTicket before it's treated
// as abandoned rather than "still waiting to be created" — see the
// ROOT CAUSE comment on syncPendingRunTicketPhotoAtom below. Generous on
// purpose: long enough that a real offline save queued right before a
// tab closes has every reasonable chance to actually sync first, short
// enough that a genuinely abandoned draft capture doesn't keep silently
// re-triggering on every app load for days.
const PENDING_PHOTO_ABANDON_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Uploads one pending run-ticket photo to S3 and attaches it to its
 * RunTicket via runTicket_CreateUpdate, then clears the local copy —
 * deliberately not the same trigger as the ticket record's own queued
 * mutation succeeding (see offlineDb.ts's PHOTO_STORE_NAME comment and
 * the REMOVED note on offlineQueue.ts's applySyncResultAtom, both fixed
 * 2026-08-20). The photo only stops being "pending" once it's actually
 * durable on the backend.
 *
 * Sends a sparse `{id, ticketImageS3Data}` payload, not the ticket's
 * full field set — CONFIRMED 2026-08-27 via a live test against the
 * real backend (see project memory / run_ticket_photo_attach_race.md)
 * that runTicket_CreateUpdate is a genuine partial update: omitting a
 * field leaves it alone rather than nulling it out. This used to
 * resend every other field too, defensively, on the (never actually
 * confirmed) assumption that an omitted field would get cleared — that
 * assumption is now known to be wrong, and the full resend carried a
 * real staleness risk of its own (overwriting a field the AI scan job
 * or another edit had changed since `latest` was last refreshed).
 *
 * IMPORTANT (fixed 2026-08-21): this will NOT create a RunTicket that
 * doesn't already exist in runTicketsAtom. It used to fall back to a
 * `fallbackInput` snapshot captured at photo-capture time and send that
 * through runTicket_CreateUpdate (an upsert) if the ticket wasn't found
 * — which meant this function itself could create a RunTicket row
 * before the user had ever actually saved/submitted anything. That was
 * half of a real bug Duane caught: ScanTicketButton used to also create
 * the ticket immediately on photo capture (fixed separately, see its
 * own doc comment), but even after removing that, THIS function's
 * fallback-create path would have quietly done the exact same thing on
 * its own. Now: no existing record in runTicketsAtom simply means
 * "nothing to attach to yet" — retried later (see
 * syncAllPendingRunTicketPhotosAtom / useOfflinePhotoSync /
 * useOfflineSync's post-flush retry) once the record actually exists,
 * never created here.
 *
 * Throws on failure so callers can tell "still pending, will retry"
 * apart from "done" — except for a genuinely permanent mutation
 * failure, which is handled terminally right here (see the
 * permanent-failure branch below) rather than left to retry forever.
 * CHANGED 2026-08-22 (Duane, explicit call): that branch does NOT
 * delete the S3 object — the client never deletes photos, full stop.
 * See lib/runTicketPhotoUpload.ts's header comment for the reasoning;
 * a photo whose attach never succeeds is left in S3, unreferenced, for
 * a periodic server-side cleanup job to reclaim.
 *
 * CHANGED 2026-09-09 (Duane's report: "when I rescan ticket, the status
 * doesn't go back to Pending"): this used to send only
 * `{id, ticketImageS3Data}` — deliberately never touching scanStatus,
 * on the theory that it's exclusively AI-scan-owned (see
 * runTicketMapping.ts's normalizeScanStatus/denormalizeScanStatus doc
 * comments). That theory didn't account for RETAKING a photo on a
 * ticket that already went through a scan: RunTicketFormPanel's
 * "Retake ticket photo" (or ScanTicketButton's "Rescan") swaps in a new
 * photo, but nothing ever told the backend the previous scan result
 * (whatever scanStatus the ticket already carried — 'Scanned',
 * 'Approved', 'NeedsReview', even 'Failed') is now stale — so both the
 * server row and (once it caught up via cache update) the UI kept
 * showing the OLD status for a ticket that, from the photo's
 * perspective, hadn't been scanned yet. Every attach now also sends
 * `scanStatus: 'Pending'` — the same value blankTicketInput
 * (runTicketMapping.ts) already seeds a brand-new ticket with — so a
 * fresh photo always resets the ticket back to "awaiting scan"
 * regardless of whatever the previous scan concluded. Harmless no-op
 * for the brand-new-ticket path (already 'Pending' there); for a
 * rescan of a reviewed/approved ticket, this is the actual fix. See
 * RunTicket_AttachPhoto.graphql's own 2026-09-09 note for why this now
 * reaches runTicketsAtom immediately instead of waiting on a separate
 * refetch/poll, and RunTicketFormPanel.handlePhotoChange for the
 * matching local-draft update (so the "Needs Review" banner/Approve
 * flow don't act on stale scanStatus while this attach is in flight).
 */
export const syncPendingRunTicketPhotoAtom = atom(null, (get, set, photo: PendingPhoto) => {
   // See inFlightSyncs' own comment above — a second call for a photo
   // that's already mid-sync just gets handed the SAME promise instead
   // of starting a redundant, concurrent second upload+attach.
   const existing = inFlightSyncs.get(photo.entityKey);
   if (existing) return existing;

   const run = async (): Promise<PhotoSyncResult> => {
      // Checked BEFORE uploading (reordered 2026-09-11 — see the
      // ROOT CAUSE comment just below) — a photo whose RunTicket doesn't
      // exist can't be attached regardless of whether the upload itself
      // succeeds, so there's no reason to spend an S3 PUT finding that
      // out every single pass.
      const found = get(runTicketsAtom).find((t) => t.id === photo.runTicketId);
      // FIX: runTicketsAtom only holds the reconciliation window (recent
      // pickup dates), so a retake on an older ticket opened from the Run
      // Tickets page was never found here — the sync threw 'RunTicket not
      // created yet' (swallowed) and no upload/AttachPhoto call was ever
      // made. An entityKey of exactly `run-ticket:<id>` is an EXISTING
      // server ticket (new ones are `run-ticket:new:<id>`), so it needs no
      // atom lookup — only id is required to attach.
      const isExistingTicketKey = photo.entityKey === `run-ticket:${photo.runTicketId}`;
      const latest =
         found ??
         (isExistingTicketKey
            ? ({id: photo.runTicketId, wellId: '', wellName: '', ticketNumber: ''} as RunTicket)
            : undefined);
      if (!latest) {
         // ROOT CAUSE (2026-09-11, Duane: "why does uploadRunTicketPhoto
         // get called on refresh, I wasn't doing anything with run
         // tickets"): useOfflinePhotoSync is mounted globally (App.tsx)
         // and processes EVERY entry in the pending-photos store on every
         // app load once online+authed, regardless of what page you're
         // on. A photo captured on a brand-new, not-yet-saved Run Ticket
         // (entityKey `run-ticket:new:...`, see ScanTicketButton.tsx/
         // RunTicketFormPanel.tsx) is only ever cleaned up by an in-app
         // Cancel/Back/retake — a hard refresh or a closed tab skips all
         // of that, so the entry survives in IndexedDB forever. This
         // branch used to just throw every time with no way to tell "will
         // exist any moment" apart from "never will", so an entry like
         // that retried indefinitely, on every single load, silently
         // (unlike a genuine attach failure, which does toast + clean up).
         //
         // PENDING_PHOTO_ABANDON_MS below gives a genuinely in-flight save
         // (RunTicket_CreateUpdate still queued/in transit, or this simply
         // running before useRunTicketReconciliation's own first
         // RunTicket_AllWhere fetch has resolved — see
         // useOfflinePhotoSync.ts's runTicketsLoadingAtom gate for the
         // other half of that race) real time to land before giving up —
         // but once a photo has sat unmatched longer than that, its
         // RunTicket is never coming, and this now cleans it up (+ toasts)
         // the same way a genuine attach failure already does, instead of
         // retrying forever. Entries written before capturedAt existed
         // read as `undefined` here (`?? 0`), which correctly treats them
         // as already past the threshold — exactly right for an
         // already-orphaned pre-existing entry.
         if (Date.now() - (photo.capturedAt ?? 0) > PENDING_PHOTO_ABANDON_MS) {
            await deletePendingPhoto(photo.entityKey);
            console.error(
               'Run ticket photo abandoned — its RunTicket was never created; local pending copy dropped',
               photo.runTicketId,
            );
            set(pushNotificationAtom, {
               message:
                  'A scanned ticket photo was never saved to a run ticket and has been discarded — rescan it if this was a mistake.',
               severity: 'warning',
            });
            return {status: 'failed', runTicketId: photo.runTicketId};
         }
         console.info(
            'Run ticket photo pending — its RunTicket does not exist yet — will attach once it does',
            photo.runTicketId,
         );
         throw new Error('RunTicket not created yet');
      }

      let imageLocation: string;
      try {
         imageLocation = await uploadRunTicketPhoto(photo.runTicketId, photo.blob, photo.contentType);
      } catch (err) {
         // Upload itself failed (offline, or a real S3 error) — leave the
         // pending photo in IndexedDB untouched to retry on the next pass.
         console.warn('Run ticket photo upload failed, will retry later', photo.runTicketId, err);
         throw err;
      }

      const runTicket = {id: latest.id, ticketImageS3Data: {imageLocation}, scanStatus: 'Pending'};

      // ROOT CAUSE FOUND 2026-08-21 (Duane, via a captured network trace):
      // apolloClient.ts's `defaultOptions` only sets `errorPolicy: 'all'`
      // for `watchQuery`/`query` — mutations were left on Apollo Client's
      // own default of `errorPolicy: 'none'`. That policy means: if a
      // mutation response comes back with BOTH a valid `data` payload AND
      // any `errors` entry (even something cosmetic — e.g. a nullable
      // field the resolver didn't populate on this call, unrelated to
      // whether the write itself happened), Apollo discards `data` and
      // throws, indistinguishable from a real failure. Fixed by passing
      // `errorPolicy: 'all'` on this call (kept below) so a response with
      // errors alongside real data doesn't get thrown away.
      //
      // SECOND ROOT CAUSE FOUND 2026-08-21 (Duane, a follow-up network
      // trace, same day): that fix alone did NOT stop the retry-then-
      // delete pattern — still 3 identical "RunTicket_CreateUpdate with
      // image" calls followed by a DeleteObject, with errorPolicy: 'all'
      // already in place. That rules out Apollo discarding data; it means
      // the SERVER's own response really did have `data: null`, all 3
      // times, deterministically — no client-side errorPolicy setting can
      // recover data that was never there. Leading theory: every RunTicket
      // field is nullable in the schema except id/isDeleted/
      // createdByUserId/createdDateTime/updatedByUserId/updatedDateTime —
      // RunTicket_CreateUpdate.graphql's response selection asked for most
      // of those. GraphQL null-propagates a failed non-nullable field up
      // to the nearest nullable ancestor, which here is the whole
      // `runTicket_CreateUpdate` object — so if any one of those fields'
      // resolvers failed on this particular backend code path (while the
      // underlying write still went through, matching Duane's own
      // observation that imageLocation ends up correct every time), the
      // entire response would come back null on every attempt, not just
      // sometimes. Not confirmed which field — flagged to Duane to check
      // one failing response's `errors[].message`/`.path` in DevTools.
      //
      // Fix: this call now uses RunTicket_AttachPhoto.graphql (a sibling
      // operation, see its own header comment) instead of
      // RunTicket_CreateUpdate, selecting only `id` in the response — the
      // only field this function needed anyway (it already relies on a
      // separate RunTicket_All refetch/poll to get the fully-populated
      // record).
      // That removes every field that could possibly null-propagate the
      // whole object. `errorPolicy: 'all'` is kept regardless, since it's
      // still correct/harmless on its own.
      const RETRY_DELAYS_MS = [1000, 2500];
      let succeeded = false;
      let lastErr: unknown;
      // Captured on success so this can hand the caller a full, freshly-
      // scanned RunTicket instead of just the imageLocation string — see
      // the PhotoSyncResult doc comment above.
      let attachedRow: RunTicket_AttachPhotoMutation['runTicket_CreateUpdate'] = null;
      for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
         try {
            const result = await apolloClient.mutate({
               mutation: RunTicket_AttachPhotoDocument,
               variables: {runTicket},
               errorPolicy: 'all',
            });
            if (result.data?.runTicket_CreateUpdate) {
               if (result.errors?.length) {
                  console.warn(
                     'RunTicket_CreateUpdate (attach) returned errors alongside a successful write — treating as success',
                     photo.runTicketId,
                     result.errors,
                  );
               }
               attachedRow = result.data.runTicket_CreateUpdate;
               succeeded = true;
               break;
            }
            // No data at all — this one really didn't write. Treat like
            // a caught error below (retry, or fall through to cleanup).
            lastErr = result.errors?.[0] ?? new Error('runTicket_CreateUpdate returned no data');
         } catch (err) {
            // errorPolicy: 'all' only leaves network-level failures able
            // to throw (a GraphQL error alongside data resolves normally
            // above, it doesn't land here) — so any catch here is a real
            // connectivity problem, not a partial/cosmetic GraphQL error.
            lastErr = err;
            // Uploaded but couldn't attach yet — the object is already at
            // its final deterministic key, so leave the pending photo in
            // place; the next retry re-attaches (or re-uploads the same
            // key, a harmless overwrite) rather than orphaning it. Not
            // one of the short in-process retries below — offline can
            // stay offline far longer than a couple of seconds, so this
            // is left to the existing reconnect-driven retry instead.
            throw err;
         }
         if (attempt >= RETRY_DELAYS_MS.length) break;
         console.warn(
            `Run ticket photo attach returned no data (attempt ${attempt + 1}), retrying shortly`,
            photo.runTicketId,
            lastErr,
         );
         await new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS_MS[attempt]));
      }

      if (!succeeded) {
         // A real failure attaching a real upload — no data came back
         // even after the retries above. CHANGED 2026-08-22 (Duane,
         // explicit call): this does NOT delete the S3 object. The
         // client never deletes photos — the only thing dropped here is
         // the LOCAL pending-photo queue entry, so this doesn't retry
         // forever against a request the server will never accept. The
         // object itself stays at its deterministic key, unreferenced
         // (the RunTicket row's imageLocation never got set), for a
         // periodic server-side cleanup job to reclaim once one exists —
         // see lib/runTicketPhotoUpload.ts's header comment. Surfaced as
         // a toast (not just a console.error) — this exact failure mode
         // silently disappearing into devtools-only logging is what let
         // it go unnoticed before.
         await deletePendingPhoto(photo.entityKey);
         console.error(
            'Run ticket photo attach failed permanently; local pending copy dropped, S3 object left in place',
            photo.runTicketId,
            lastErr,
         );
         set(pushNotificationAtom, {
            message: `A run ticket's scanned photo couldn't be saved (ticket #${latest.ticketNumber || latest.id}). It was not attached — you may need to rescan it.`,
            severity: 'warning',
         });
         return {status: 'failed', runTicketId: photo.runTicketId};
      }

      await deletePendingPhoto(photo.entityKey);
      // toRunTicket is structurally compatible with attachedRow even though
      // its declared parameter type comes from RunTicket_AllWhereQuery —
      // RunTicket_AttachPhoto.graphql's selection was widened 2026-09-10 to
      // match RunTicket_CreateUpdate.graphql/RunTicket_AllWhere.graphql's
      // field list exactly (confirmed via the generated types), so this is
      // a real, checked mapping rather than a cast.
      const ticket = attachedRow ? toRunTicket(attachedRow, new Map([[latest.wellId, latest.wellName]])) : null;
      return {status: 'attached', runTicketId: photo.runTicketId, imageLocation, ticket: ticket ?? latest};
   };

   const promise = run().finally(() => {
      inFlightSyncs.delete(photo.entityKey);
   });
   inFlightSyncs.set(photo.entityKey, promise);
   return promise;
});

/**
 * Runs the whole not-yet-synced backlog — called on reconnect (see
 * useOfflinePhotoSync), right after useOfflineSync's own mutation-queue
 * flush (so a RunTicket that just got created via the queue gets its
 * photo attached in the same pass rather than waiting for a second
 * reconnect), and right after Submit Report actually creates a pending
 * ticket while online (DailyReportPage.tsx/DailyReportPanel.tsx). One
 * photo's failure doesn't block the rest of the backlog from being
 * attempted.
 *
 * Returns permanentlyFailedIds: the runTicketIds whose photo hit the
 * *permanent* failure path inside syncPendingRunTicketPhotoAtom (local
 * pending copy already dropped + toasted, not retried — the S3 object
 * itself is left in place, see that function's own doc comment) — as
 * opposed to a transient one that just threw to retry later. A caller
 * that just saved/reviewed one specific ticket (e.g.
 * RunTicketsPage.handleSaveTicket) checks whether its own id is in this
 * list to decide whether to keep that ticket's drawer open instead of
 * closing it on top of an error the user hasn't seen a chance to act on
 * (Duane, 2026-08-21 — Approve & Save closed the drawer even though the
 * photo attach had just failed and needed a rescan).
 *
 * ADDED 2026-09-10: also returns attachedTickets — the full, freshly-
 * mapped RunTicket for every photo that attached successfully this pass
 * (per PhotoSyncResult's own doc comment). A caller that just
 * saved/reviewed one specific ticket can look this up by id to get the
 * real post-scan values (scanStatus and every OCR-extracted field)
 * without waiting on a separate refetch — this is what actually carries
 * a just-completed scan's results back to RunTicketsPage's drawer.
 */
export const syncAllPendingRunTicketPhotosAtom = atom(null, async (_get, set) => {
   const photos = await getAllPendingPhotos();
   if (photos.length === 0) return {permanentlyFailedIds: [] as string[], attachedTickets: [] as RunTicket[]};
   set(photoSyncStateAtom, 'syncing');
   let hadFailure = false;
   const permanentlyFailedIds: string[] = [];
   const attachedTickets: RunTicket[] = [];
   for (const photo of photos) {
      try {
         const result = await set(syncPendingRunTicketPhotoAtom, photo);
         if (result.status === 'failed') {
            permanentlyFailedIds.push(photo.runTicketId);
         } else {
            attachedTickets.push(result.ticket);
         }
      } catch {
         hadFailure = true;
      }
   }
   set(photoSyncStateAtom, hadFailure ? 'error' : 'idle');
   return {permanentlyFailedIds, attachedTickets};
});
